import React from "react";
import * as UI from "../components/ui";
import { COMPONENT_REGISTRY } from "./componentRegistry";
import ComponentEntry from "./ComponentEntry";
// NOTE: the V1 stylesheet (style-guide.scss) is deliberately NOT imported here — its bare element
// rules bleed into the whole V2 page. The mould's .sg-* atoms are styled from tokens in styleguide-v2.scss.

/**
 * LiveComponents — the registry-driven lane of Style Guide V2 (Fontaine M07).
 *
 * Every entry here IS the real component from `components/ui`, rendered through the ComponentEntry
 * mould (header · blurb · live demo · snippet · props · do/don't). The hand-drawn V2 mock-ups above
 * stay as the TARGET until each primitive reaches parity in Phase 1; when a primitive is done, its
 * mock-up is removed and this lane is the only truth. Nothing in this file may re-style a component.
 *
 * The coverage panel lists every export of `components/ui` and whether it has a registry entry —
 * red means "exists in code, undocumented" — so the guide cannot silently drift from the code.
 */

// Layout re-exports live in components/layout and are documented under page chrome, not here.
const LAYOUT_EXPORTS = new Set(["PageLayout", "PageHeader", "Topbar", "SubTopbar", "MobileTopbar", "Sidebar"]);
// Helpers that are not visual primitives.
const NON_VISUAL = new Set(["ICONS", "Show", "useAutosave", "useToast", "useAlert", "ToastProvider", "MenuList", "ConfirmDialog"]); // Show renders nothing of its own (responsive gate)

export function coverage() {
  const documented = new Set(COMPONENT_REGISTRY.flatMap((c) => [c.importName || c.name, ...(c.covers || [])]));
  return Object.keys(UI)
    .filter((n) => !NON_VISUAL.has(n))
    .sort()
    .map((name) => ({
      name,
      tier: LAYOUT_EXPORTS.has(name) ? "layout" : "primitive",
      documented: documented.has(name),
    }));
}

export default function LiveComponents() {
  const rows = coverage();
  const missing = rows.filter((r) => !r.documented && r.tier === "primitive");
  return (
    <div className="sgv2__live-lane">
      <div className="sgv2__coverage" data-visual-mask={undefined}>
        <div className="sgv2__coverage-head">
          <span className="sgv2__eyebrow">Coverage</span>
          <span className={`sgv2__coverage-score${missing.length ? " is-short" : ""}`}>
            {rows.filter((r) => r.tier === "primitive" && r.documented).length} / {rows.filter((r) => r.tier === "primitive").length} primitives documented
          </span>
        </div>
        <ul className="sgv2__coverage-list">
          {rows.map((r) => (
            <li key={r.name} className={`sgv2__coverage-item${r.documented ? " is-ok" : r.tier === "layout" ? " is-layout" : " is-missing"}`}>
              <span className="sgv2__coverage-dot" aria-hidden="true" />
              <code>{r.name}</code>
              <span className="sgv2__coverage-note">
                {r.documented ? "documented" : r.tier === "layout" ? "layout — see page chrome" : "no registry entry"}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="sgv2__live-entries">
        {COMPONENT_REGISTRY.map((c) => (
          <ComponentEntry key={c.name} {...c} count={c.count ?? c.sites?.length ?? 0}>
            {c.demo}
          </ComponentEntry>
        ))}
      </div>
    </div>
  );
}
